import { RouteComponentProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { HeaderBar } from 'components/HeaderBar';
import PageLayout from 'components/PageLayout';
import { LoadingAndErrors } from 'components/LoadingAndErrors';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { useAuthState } from 'state/useAuthState';
import Unauthorized from 'components/Unauthorized';
import { useGeoEvent } from 'features/geoEvents/hooks/useGeoEvent';
import ReportForm from './ReportForm';

type ContentProps = {
  geoEvent: Wildfire;
};

const Content = (props: ContentProps): JSX.Element => {
  const { geoEvent } = props;
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('addIncidentReport.title')}</title>
      </Helmet>
      <ReportForm geoEvent={geoEvent} />
    </>
  );
};

const AddIncidentReport = (
  props: RouteComponentProps<{ geoEventId: string }>,
): JSX.Element => {
  const {
    match: {
      params: { geoEventId },
    },
  } = props;
  const { t } = useTranslation();
  const {
    permissions: { canReport },
  } = useAuthState();

  const { geoEvent, isGeoEventLoading, isGeoEventFetchError } = useGeoEvent({
    id: Number(geoEventId),
  });

  if (!canReport) {
    return (
      <PageLayout data-cname="AddIncidentReportPage">
        {{
          content: <Unauthorized />,
          headerBar: (
            <HeaderBar title={t('unauthorized.title')} action="back" />
          ),
        }}
      </PageLayout>
    );
  }

  if (isGeoEventLoading) {
    return (
      <PageLayout data-cname="AddIncidentReportPage">
        {{
          content: <LoadingAndErrors isLoading />,
          headerBar: <HeaderBar title="Loading" action="back" />,
        }}
      </PageLayout>
    );
  }

  if (isGeoEventFetchError || !geoEvent) {
    return (
      <PageLayout data-cname="AddIncidentReportPage">
        {{
          content: <LoadingAndErrors />,
          headerBar: <HeaderBar title="Error..." action="back" />,
        }}
      </PageLayout>
    );
  }

  return (
    <PageLayout data-cname="AddIncidentReportPage">
      {{
        content: <Content geoEvent={geoEvent} />,
        headerBar: (
          <HeaderBar title={t('addIncidentReport.title')} action="back" />
        ),
      }}
    </PageLayout>
  );
};

export default AddIncidentReport;
